import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThrowResult, ThrowErrorResult, ErrorFieldError } from '../types/errorDemo';

// ─── Keyframe CSS ─────────────────────────────────────────────────────────────

const TOAST_ANIMATION = `
@keyframes fadeInOut {
  0%   { opacity: 0; transform: translateY(8px); }
  15%  { opacity: 1; transform: translateY(0); }
  80%  { opacity: 1; }
  100% { opacity: 0; }
}
`;

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
}

function CopyToast({ message }: ToastProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-8)',
        right: 'var(--spacing-8)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderLeft: '4px solid var(--color-warning)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        boxShadow: 'var(--shadow-md)',
        zIndex: 9999,
        animation: 'fadeInOut 2.5s ease forwards',
      }}
    >
      {message}
    </div>
  );
}

// ─── Field errors table ───────────────────────────────────────────────────────

interface FieldErrorsTableProps {
  fieldErrors: ErrorFieldError[];
}

function FieldErrorsTable({ fieldErrors }: FieldErrorsTableProps) {
  return (
    <div style={{ overflowX: 'auto', marginTop: 'var(--spacing-2)' }}>
      <table
        aria-label="fieldErrors"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-xs)',
        }}
      >
        <thead>
          <tr>
            {['field', 'code', 'message', 'rejectedValue'].map((h) => (
              <th
                key={h}
                scope="col"
                style={{
                  padding: 'var(--spacing-1) var(--spacing-2)',
                  textAlign: 'left',
                  background: 'var(--color-surface-muted)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fieldErrors.map((fe, idx) => (
            <tr key={idx}>
              {[fe.field, fe.code, fe.message, fe.rejectedValue ?? '—'].map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    borderBottom:
                      idx < fieldErrors.length - 1
                        ? '1px solid var(--color-surface-border)'
                        : 'none',
                    fontFamily: 'var(--font-family-mono)',
                    color: 'var(--color-text-secondary)',
                    wordBreak: 'break-all',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Row helper ───────────────────────────────────────────────────────────────

interface RowProps {
  label: string;
  children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-2) 0',
        borderBottom: '1px solid var(--color-surface-border)',
        alignItems: 'start',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-family-mono)',
          paddingTop: 2,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
          fontFamily: 'var(--font-family-mono)',
          wordBreak: 'break-all',
        }}
      >
        {children}
      </span>
    </div>
  );
}

// ─── Result content ───────────────────────────────────────────────────────────

interface ResultContentProps {
  data: ThrowErrorResult;
  onCopyTrace: () => void;
}

function ResultContent({ data, onCopyTrace }: ResultContentProps) {
  const { t } = useTranslation();
  // Determine status color — only for the status number itself
  const statusColor =
    data.httpStatus >= 500
      ? 'var(--color-danger)'
      : data.httpStatus >= 400
        ? 'var(--color-warning)'
        : 'var(--color-success)';

  return (
    <div>
      <Row label="status">
        <span style={{ color: statusColor, fontWeight: 700 }}>
          {data.httpStatus}
        </span>
      </Row>
      <Row label="code">
        <code style={{ color: 'var(--color-brand-primary)' }}>{data.code}</code>
      </Row>
      <Row label="message">{data.message}</Row>
      {data.messageEn && <Row label="message (en)">{data.messageEn}</Row>}
      {data.detail && <Row label="detail">{data.detail}</Row>}
      {data.fieldErrors && data.fieldErrors.length > 0 && (
        <div
          style={{
            padding: 'var(--spacing-2) 0',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            fieldErrors
          </span>
          <FieldErrorsTable fieldErrors={data.fieldErrors} />
        </div>
      )}
      {data.traceId && (
        <Row label="traceId">
          <button
            type="button"
            onClick={onCopyTrace}
            title={t('errorDemo.result.copyTraceTooltip')}
            aria-label={`traceId ${data.traceId} ${t('errorDemo.result.copyTraceTooltip')}`}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-brand-primary)',
              textDecoration: 'underline dotted',
              padding: 0,
            }}
          >
            {data.traceId}
          </button>
        </Row>
      )}
      {data.timestamp && <Row label="timestamp">{data.timestamp}</Row>}
    </div>
  );
}

// ─── ThrowResultCard ──────────────────────────────────────────────────────────

interface ThrowResultCardProps {
  result: ThrowResult;
}

/**
 * Displays the result of an intentional throw demo.
 * Uses gray/yellow neutral styling — NOT red ErrorBanner.
 * role="status" (not role="alert") so screen readers announce without interrupting.
 */
export function ThrowResultCard({ result }: ThrowResultCardProps) {
  const { t } = useTranslation();
  const [showToast, setShowToast] = useState(false);

  const handleCopyTrace = async () => {
    if (result.status !== 'result') return;
    const traceId = result.data.traceId;
    if (!traceId) return;
    try {
      await navigator.clipboard.writeText(traceId);
    } catch {
      // fallback — ignore clipboard errors in test env
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  return (
    <>
      <style>{TOAST_ANIMATION}</style>

      <div
        role="status"
        aria-live="polite"
        aria-label={t('errorDemo.result.title')}
        style={{
          border: '1px solid var(--color-surface-border)',
          borderLeft: '4px solid var(--color-warning)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-muted)',
        }}
      >
        {/* Card header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            {t('errorDemo.result.title')}
          </span>
          {result.status === 'result' && (
            <span
              style={{
                display: 'inline-block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-warning)',
                color: 'var(--color-text-inverse)',
              }}
            >
              {t('errorDemo.result.intendedBadge')}
            </span>
          )}
          {result.status === 'success' && (
            <span
              style={{
                display: 'inline-block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-success)',
                color: 'var(--color-text-inverse)',
              }}
            >
              {t('errorDemo.result.successBadge')}
            </span>
          )}
        </div>

        {/* Card body */}
        <div style={{ padding: 'var(--spacing-4)' }}>
          {result.status === 'idle' && (
            <p
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                textAlign: 'center',
                padding: 'var(--spacing-8) 0',
              }}
            >
              {t('errorDemo.result.idle')}
            </p>
          )}

          {result.status === 'loading' && (
            <div
              aria-busy="true"
              aria-label={t('errorDemo.result.loading')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
                padding: 'var(--spacing-4) 0',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  border: '2px solid var(--color-surface-border)',
                  borderTopColor: 'var(--color-brand-primary)',
                  borderRadius: 'var(--radius-full)',
                  animation: 'spin 0.75s linear infinite',
                }}
                aria-hidden="true"
              />
              {t('errorDemo.result.loading')}
            </div>
          )}

          {result.status === 'result' && (
            <ResultContent data={result.data} onCopyTrace={handleCopyTrace} />
          )}

          {result.status === 'success' && (
            <p
              style={{
                color: 'var(--color-success)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {result.message}
            </p>
          )}

          {result.status === 'error' && (
            <p
              style={{
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {result.message}
            </p>
          )}

          {/* Learning hint — shown after result */}
          {result.status === 'result' && (
            <div
              style={{
                marginTop: 'var(--spacing-4)',
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              ⓘ {t('errorDemo.result.learnHint')}
            </div>
          )}
        </div>
      </div>

      {showToast && (
        <CopyToast message={t('errorDemo.result.copyTraceToast')} />
      )}
    </>
  );
}
